import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Divider
} from '@mui/material';
import React, { useState } from 'react';
import './CommentsDialog.scss';

interface Comment {
  id: number;
  author: string;
  timestamp: string;
  content: string;
}

interface CommentsDialogProps {
  open: boolean;
  onClose: () => void;
  comments: Comment[];
  onAddComment?: (content: string) => void; // optional callback for new comment
  isAdding?: boolean; // optional loading flag
}

const CommentsDialog = ({
  open,
  onClose,
  comments,
  onAddComment,
  isAdding
}: CommentsDialogProps) => {
  const [newComment, setNewComment] = useState('');

  const handleDialogClose = (event: any, reason: string) => {
    // prevent close on backdrop click, allow close on X button
    if (reason !== 'backdropClick') onClose();
  };

  const handleAdd = () => {
    const content = newComment.trim();
    if (!content) return;
    onAddComment?.(content);
    // clear only if consumer does not handle loading state
    if (!isAdding) setNewComment('');
  };

  const isAddDisabled = !newComment.trim() || isAdding;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      className="comments-dialog"
      maxWidth="md"
      disableEscapeKeyDown
    >
      <DialogTitle>
        <div className="comments-dialog__header">
          <h2 className="title">Comments</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <i className="icon-close" />
          </button>
        </div>
      </DialogTitle>

      <DialogContent className="dialog-content">
        <div className="comments-list">
          {comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-header">
                <span className="author">{comment.author}</span>
                <span className="timestamp">{comment.timestamp}</span>
              </div>
              <div className="comment-content">{comment.content}</div>
            </div>
          ))}
        </div>

        <Divider className="section-divider" />

        <div className="new-comment">
          <label className="new-comment__label">New Comment</label>
          <TextField
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment.."
            multiline
            minRows={3}
            fullWidth
            variant="outlined"
            className="new-comment__input"
          />
        </div>
      </DialogContent>

      <DialogActions>
        <div className="buttons">
          <Button
            className="btn-black"
            onClick={handleAdd}
            disabled={isAddDisabled}
          >
            <span>{isAdding ? 'Adding…' : 'Add'}</span>
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CommentsDialog;
